<template>
  <div class="tab-content">
    <div class="flex jcsb" style="padding-right: 88px">
      <div class="tab_two_title" style="margin-bottom: 18px">

      </div>
      <a-button type="primary" ghost class="ml24 mt22 mb18"><i
          class="iconfont icon-quanbuxiazaiicon mr5"></i> 全部下载</a-button>
    </div>
    <div class="table-container">
      <div class="info-area">
        <!-- 材料列表 -->
        <div v-for="item in materialsList" :key="item.id" class="document-item">
          <div class="document-header">
            <div class="document-title">
              <span>{{ item.materialCode }}. {{ item.materialTitle }}</span>
            </div>
          </div>
          <div class="document-upload">
            <MaterialFileManager
                :materialId="item.id"
                :initialFiles="item.files || []"
                :supportedFormats="item.supportedFormats || []"
                :edit="false"
                @update:files="(files, materialId) => handleFilesUpdate(materialId, files)"
                @upload-success="handleUploadSuccess"
                @delete="handleFileDelete"
            />
          </div>
        </div>
      </div>
      
      <!-- 统一的总审核表单 -->
      <div class="total-review-section">
        <div class="review-form">
          <div class="form-item">
            <label class="form-label">审核结果：</label>
            <div class="radio-group">
              <label class="radio-option">
                <input type="radio" name="totalReviewResult" value="1"
                       v-model="approverData.result"
                       @change="handleReviewResultChange(1)"
                       :disabled="materialFirstApproveTask && (materialFirstApproveTask.completed === 1 || materialFirstApproveTask.endTime)" />            
                <span>通过</span>
              </label>
              <label class="radio-option">
                <input type="radio" name="totalReviewResult" value="0"
                       v-model="approverData.result"
                       @change="handleReviewResultChange(0)"
                       :disabled="materialFirstApproveTask && (materialFirstApproveTask.completed === 1 || materialFirstApproveTask.endTime)" />            
                <span>不通过</span>
              </label>
            </div>
          </div>
          
          <!-- 审核意见/不通过原因 -->
          <div class="form-item">
            <label class="form-label">{{ reviewLabelText }}：</label>
            <textarea class="review-textarea" rows="3" 
                      :placeholder="reviewPlaceholderText"
                      v-model="approverData.comment"
                      :disabled="materialFirstApproveTask && (materialFirstApproveTask.completed === 1 || materialFirstApproveTask.endTime)">
            </textarea>
          </div>
          
          <!-- 操作按钮，仅在未审核时显示 -->
          <div v-if="userTaskPermissionList.includes('material_first_approve')">
            <div v-if="!materialFirstApproveTask || !materialFirstApproveTask.endTime" class="action-buttons">
              <button class="btn-cancel" @click="resetTotalReview">重置</button>
              <button class="btn-confirm" @click="submitApprove">提交</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup name="ApplicationMaterialsTab">
import { defineProps, ref, onMounted, reactive, watch, computed, defineEmits } from 'vue';
import {message} from 'ant-design-vue';
import { getApplicationMaterialsByApplicationId } from "@/api/application/applicationMaterials";
import {
  getProcessTaskByApplicationIdAndFuncCode,
  applicationApprove
} from '@/api/application/applicationProcessTask';
import MaterialFileManager from '@/components/MaterialFileManager/index.vue';

// 定义组件接收的props
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  userTaskPermissionList: {
    type: Array
  }
});

// 定义事件
const emit = defineEmits(['upload-application-info']);

//材料审核的功能编码
const funcCode = 'material_first_approve';
// 材料列表数据
const materialsList = ref([]);
const materialFirstApproveTask = ref();

// 总审核数据
const approverData = reactive({
  result: undefined, // 1: 通过, 0: 不通过
  comment: ''
});

// 计算属性用于标签文本
const reviewLabelText = computed(() => {
  return approverData.result === 1 ? '审核意见' : '不通过原因';
});

// 计算属性用于占位符文本
const reviewPlaceholderText = computed(() => {
  return approverData.result === 1 ? '请输入审核意见（可选）' : '请输入不通过原因';
});

// 处理审核结果变更
const handleReviewResultChange = (value) => {
  approverData.result = value;
  console.log('审核结果已变更:', value);
};

// 监听materialFirstApproveTask变化，回显审核数据
watch(materialFirstApproveTask, (newTask) => {
  if (newTask && (newTask.completed === 1 || newTask.endTime)) {
    // 如果任务已完成，回显审核结果和意见
    approverData.result = newTask.result;
    approverData.comment = newTask.comment;
  }
}, { immediate: true });

// 监听approverData.result变化，确保UI更新
watch(() => approverData.result, (newResult) => {
  console.log('审核结果已更新:', newResult);
});

// 处理文件更新
const handleFilesUpdate = (materialId, files) => {
  const material = materialsList.value.find(item => item.id === materialId);
  if (material) {
    material.uploadedFiles = files;
  }
};

// 处理上传成功
const handleUploadSuccess = (file, materialId) => {
  console.log('文件上传成功:', file.name, '材料ID:', materialId);
};

// 处理文件删除
const handleFileDelete = (file, materialId) => {
  console.log('文件删除:', file.name, '材料ID:', materialId);
};

// 重置总审核
const resetTotalReview = () => {
  approverData.result = undefined;
  approverData.comment = '';
};

// 提交审核
const submitApprove = () => {
  // 验证总审核结果
  if (approverData.result == null) {
    message.warning('请选择审核结果');
    return;
  }
  
  // 验证不通过时的原因
  if (approverData.result === 0 && !approverData.comment.trim()) {
    message.warning('不通过时必须填写不通过原因');
    return;
  }
  
  // 材料初审数据
  const reviewSubmitData = {
    applicationId: props.id,
    funcCode: funcCode,
    result: approverData.result,
    comment: approverData.comment
  };
  
  // 这里可以添加审核提交的API调用逻辑
  applicationApprove(reviewSubmitData).then(res => {
    if (res.code === 200 && res.data){
      console.log('提交审核:', reviewSubmitData);
      materialFirstApproveTask.value = res.data
      message.success('审核提交成功');
      emit('update-application-info');
    }else {
      message.error('审核提交失败');
    }
  })

};

//获取材料初审的任务
const getMaterialFirstApproveTask = () => {

  getProcessTaskByApplicationIdAndFuncCode(props.id, funcCode).then((res) => {
    materialFirstApproveTask.value = res.data;
  }).catch(error => {
    console.error('获取材料初审任务失败:', error);
    message.error('获取材料初审任务失败，请稍后重试');
  });
}

const getMaterials = () => {
  getApplicationMaterialsByApplicationId(props.id).then((res) => {
    materialsList.value = res.data || [];
    console.log('获取材料列表成功:', materialsList.value);
  }).catch(error => {
    console.error('获取材料列表失败:', error);
    message.error('获取材料列表失败，请稍后重试');
  });
}

onMounted(() => {
  getMaterials();
  getMaterialFirstApproveTask();
});
</script>

<style scoped>
.tab-content {
  background-color: #fff;
  margin-bottom: 80px;
}

.table-container {
  padding: 0 20px;
  margin-top: 10px;
}

/* 材料列表样式 */
.document-item {
  border: 1px solid #f0f0f0;
  border-radius: 5px;
  padding: 12px;
  margin-bottom: 16px;
  background-color: #fafafa;
}

.document-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.document-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
}

.document-upload {
  margin-top: 10px;
}

/* 总审核表单样式 */
.total-review-section {
  margin-top: 23px;
  padding: 12px;
  background-color: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
}

.review-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.form-label {
  font-weight: 500;
  color: #262626;
  font-size: 14px;
  width: 100px;
  text-align: right;
  padding-right: 12px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-bottom: 0;
}

.radio-group {
  display: flex;
  gap: 24px;
  align-items: center;
  flex: 1;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.radio-option input[type="radio"] {
  cursor: pointer;
}

.review-textarea {
  width: 100%;
  max-width: 600px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  transition: border-color 0.3s;
  flex: 1;
}

.review-textarea:focus {
  outline: none;
  border-color: #40a9ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
  margin-left: 112px;
}

.btn-cancel, .btn-confirm {
  padding: 6px 24px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #d9d9d9;
}

.btn-cancel {
  background-color: #fff;
  color: #262626;
}

.btn-cancel:hover {
  border-color: #40a9ff;
  color: #40a9ff;
}

.btn-confirm {
  background-color: #1890ff;
  color: #fff;
  border-color: #1890ff;
}

.btn-confirm:hover {
  background-color: #40a9ff;
  border-color: #40a9ff;
}
</style>